/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { explicitlyDisableFeatures } from "publicApis/constants";
import { isBoolean } from "utils/lodashUtils";
import { trackEditEnabled } from "analytics/events";
import { getShouldRenderPersonalCommentsDropin, getShouldRenderCommentsDropin } from "selectors/overlayDropinSelectors";
import { EDIT_DROPIN_TYPE, ANALYTICS_OUTLINE, ANALYTICS_ASSISTANT } from "constants/constants";
import { isFillSignEnabled } from "../app/AppInitUtility";
import { registerFillAndSignApis } from "./FillAndSignApis";
import { trackIsEligibleUser } from "analytics/events";
import { getFeatureFlagApis } from "publicApis/featureFlag";
import { ENABLE_PREMIUM_INDICATOR } from "constants/constants";

export const setFeatureEnability = (stores, featureName, value, allConfigs) => {
  if (isBoolean(value)) {
    switch (featureName) {
      case "SEARCH": {
        stores.appStore.enableSearchFeature(value);
        break;
      }
      case "READ_ONLY_MODE": {
        stores.appStore.enableReadOnlyMode(value);
        break;
      }
      case "MULTI_DOC_MODE": {
        stores.appStore.enableMultiDocMode(value);
        // removed as part of ADCS-50001-ADCS-50158
        // stores.appStore.enableReadOnlyMode(value);
        break;
      }
      case "ZOOM": {
        stores.appStore.enableZoomFeature(value);
        stores.appStore.setExplicitlyDisabledFeature(explicitlyDisableFeatures.ZOOM, !value);
        break;
      }
      case "ROTATE_VIEW_CONTROL": {
        stores.hudStore.setRotateViewButtonVisibility(value);
        break;
      }
      case "COMMENTING": {
        stores.overlayDropinStore.setShouldRenderPersonalCommentsDropin(value);
        break;
      }
      case "ACROFORM": {
        stores.documentStore.setAcroFormVisibility(value);
        break;
      }
      case "BOOKMARKS": {
        stores.bookmarkStore.setShouldEnableBookmarks(value);
        break;
      }
      case "THUMBNAILS": {
        stores.thumbnailStore.setShouldShowThumbnailIcon(value);
        break;
      }
      case "ATTACHMENTS": {
        stores.lhpStore.attachmentsModel.setShouldShowAttachmentIcon(value);
        break;
      }
      case "DOWNLOAD": {
        stores.appStore.enableDownloadFeature(value);
        break;
      }
      case "PRINT": {
        stores.appStore.enablePrintFeature(value);
        break;
      }
      case "TEXT_SELECTION": {
        stores.appStore.enableTextSelection(value);
        break;
      }
      case "EDIT": {
        if (allConfigs && allConfigs.appConfig) {
          const editConfig = { ...allConfigs.appConfig };
          setEditConfig(stores, editConfig, value);

          // Fetching edit config from feature flag pipeline
          getFeatureFlagApis()
            .getFeatureFlag("edit-config")
            .then(editConfigFeatureFlag => {
              editConfig.usePartialEdit = editConfigFeatureFlag.usePartialEdit;
              editConfig.usePremiumIndicator = editConfigFeatureFlag.usePremiumIndicator;
              editConfig.shouldEnableEditGenAI = editConfigFeatureFlag.shouldEnableEditGenAI;
              editConfig.enableEdit = editConfigFeatureFlag.enableEdit;
              editConfig.showAdvancedFontPicker = editConfigFeatureFlag.showAdvancedFontPicker;
              editConfig.shouldPreloadEditModule = editConfigFeatureFlag.shouldPreloadEditModule;
              editConfig && editConfig.enableEdit && setEditConfig(stores, editConfig, value);
            });
        }
        break;
      }
      case "CROP_PAGES": {
        value && stores.overlayDropinStore.setShouldRenderCropPagesDropin(value);
        getFeatureFlagApis()
          .getFeatureFlag("edit-config")
          .then(editConfig => {
            if (editConfig && isBoolean(editConfig.enableCropPages) && editConfig.enableCropPages) {
              stores.overlayDropinStore.setShouldPreloadEditModule(editConfig.shouldPreloadEditModule);
              stores.overlayDropinStore.setShouldRenderCropPagesDropin(editConfig.enableCropPages);
            }
          });
        break;
      }
      case "NUMBER_PAGE": {
        let setRenderNumberPagesDropin = false;
        if (allConfigs && allConfigs.appConfig && isBoolean(allConfigs.appConfig.enableNumberPages)) {
          setRenderNumberPagesDropin = allConfigs.appConfig.enableNumberPages;
        }
        setRenderNumberPagesDropin &&
          stores.overlayDropinStore.setShouldRenderNumberPagesDropin(setRenderNumberPagesDropin);
        getFeatureFlagApis()
          .getFeatureFlag("edit-config")
          .then(editConfig => {
            if (editConfig && isBoolean(editConfig.enableNumberPages)) {
              setRenderNumberPagesDropin = editConfig.enableNumberPages;
            }
            if (editConfig && isBoolean(editConfig.shouldPreloadEditModule) && editConfig.enableNumberPages) {
              stores.overlayDropinStore.setShouldPreloadEditModule(editConfig.shouldPreloadEditModule);
            }
            setRenderNumberPagesDropin &&
              stores.overlayDropinStore.setShouldRenderNumberPagesDropin(setRenderNumberPagesDropin);
          });
        break;
      }
      case "READ_ALOUD": {
        stores.readAloudStore.enableReadAloud(value);
        break;
      }
      case "ACROFORM_VALIDATION_FORMATTING": {
        stores.appStore.setAcroformValidationFormatting(value);
        break;
      }
      case "PDF_JAVASCRIPT": {
        stores.appStore.setEnablePDFJavaScriptFeatureFlag(value);
        break;
      }
      case "OVERPRINT_SUPPORT": {
        stores.appStore.setEnableOverprintSupport(value);
        break;
      }
      case "GENAI_QNA": {
        stores.appStore.setShouldEnableQnA(value);
        break;
      }
      case "GENAI_OUTLINE": {
        stores.appStore.setShouldEnableOutline(value);
        break;
      }
      case "ASSISTANT_PROMO": {
        stores.appStore.setShouldShowAnimation(value);
        break;
      }
      default: {
        console.error("Feature can't be enabled or disabled");
      }
    }
  }

  if (typeof value === "object") {
    switch (featureName) {
      case "DIGITAL_SIGNATURE_CONFIG": {
        const { enableValidation, enableBannerVisibility, externalCertificateValidator } = value;
        stores.digSigStore.setEnableDigSig(!!enableValidation);
        stores.digSigStore.setDigSigBannerVisibility(!!enableBannerVisibility);
        stores.digSigStore.setExternalCertificateValidator(externalCertificateValidator || null);
        break;
      }
      default: {
        console.error("Feature config can't be set");
      }
    }
  }
};

const setEditConfig = (stores, editConfig, value) => {
  let usePartialEdit = false;
  let usePremiumIndicator = false;
  let shouldPreloadEditModule = false;
  if (editConfig && isBoolean(editConfig.usePartialEdit)) {
    usePartialEdit = editConfig.usePartialEdit;
  }
  if (editConfig && isBoolean(editConfig.usePremiumIndicator)) {
    usePremiumIndicator = editConfig.usePremiumIndicator;
  }
  if (editConfig && isBoolean(editConfig.shouldPreloadEditModule)) {
    shouldPreloadEditModule = editConfig.shouldPreloadEditModule;
  }
  stores.overlayDropinStore.setShouldPreloadEditModule(shouldPreloadEditModule);
  stores.appStore.setUsePremiumIndicator(usePremiumIndicator);
  if (editConfig && isBoolean(editConfig.shouldEnableEditGenAI)) {
    stores.appStore.setShouldEnableEditGenAI(editConfig.shouldEnableEditGenAI);
  }
  if (editConfig.disableEditFeatures) {
    if (
      isBoolean(editConfig.disableEditFeatures.disableFormatText) &&
      isBoolean(editConfig.disableEditFeatures.disableBBox)
    ) {
      stores.appStore.setDisableEditFeatures({
        disableFormatText: editConfig.disableEditFeatures.disableFormatText,
        disableBBox: editConfig.disableEditFeatures.disableBBox
      });
    } else if (
      isBoolean(editConfig.disableEditFeatures.disableFormatText) &&
      !isBoolean(editConfig.disableEditFeatures.disableBBox)
    ) {
      stores.appStore.setDisableEditFeatures({
        disableFormatText: editConfig.disableEditFeatures.disableFormatText
      });
    } else if (
      !isBoolean(editConfig.disableEditFeatures.disableFormatText) &&
      isBoolean(editConfig.disableEditFeatures.disableBBox)
    ) {
      stores.appStore.setDisableEditFeatures({
        disableBBox: editConfig.disableEditFeatures.disableBBox
      });
    }
  }

  if (editConfig && isBoolean(editConfig.isDemoMode)) {
    stores.overlayDropinStore.setIsDemoMode(editConfig.isDemoMode);
  }

  if (editConfig && isBoolean(editConfig.showEditOnTextSelection)) {
    stores.overlayDropinStore.setShowEditOnTextSelection(editConfig.showEditOnTextSelection);
  }

  if (value || (editConfig && isBoolean(editConfig.enableEdit))) trackEditEnabled(usePartialEdit);
  let renderEditValue = EDIT_DROPIN_TYPE.NONE;

  if (value || (editConfig && isBoolean(editConfig.enableEdit))) {
    renderEditValue = usePartialEdit ? EDIT_DROPIN_TYPE.PARTIAL : EDIT_DROPIN_TYPE.FULL;
  }
  let setAFFontpicker = false;
  if (editConfig && isBoolean(editConfig.showAdvancedFontPicker)) {
    setAFFontpicker = editConfig.showAdvancedFontPicker;
  }
  stores.appStore.setUseAFFontPicker(setAFFontpicker);
  stores.overlayDropinStore.setShouldRenderEditDropin(renderEditValue);
};

export const getFeatureConfigApis = stores => {
  function enableFeature(featureName, value, allConfigs) {
    return new Promise((resolve, reject) => {
      if (isBoolean(value)) {
        switch (featureName) {
          case "COMMENTING": {
            const isCommentingEnabled = getShouldRenderPersonalCommentsDropin(stores);
            if (value === true && !isCommentingEnabled) {
              stores.overlayDropinStore.setShouldRenderPersonalCommentsDropin(value);
            }
            resolve();
            break;
          }
          case "READ_ONLY_MODE": {
            stores.appStore.enableReadOnlyMode(value);
            break;
          }
          case "MULTI_DOC_MODE": {
            stores.appStore.enableMultiDocMode(value);
            // removed as part of ADCS-50001-ADCS-50158
            // stores.appStore.enableReadOnlyMode(value);
            break;
          }
          case "SHARED_COMMENTING": {
            const isSharedCommentingEnabled = getShouldRenderCommentsDropin(stores);
            if (value === true && !isSharedCommentingEnabled) {
              stores.overlayDropinStore.setShouldRenderCommentsDropin(value);
            }
            resolve();
            break;
          }
          case "ZOOM": {
            stores.appStore.enableZoomFeature(value);
            stores.appStore.setExplicitlyDisabledFeature(explicitlyDisableFeatures.ZOOM, !value);
            resolve();
            break;
          }
          case "SEARCH": {
            initialPageLoadedPromise(stores)
              .then(() => {
                stores.appStore.enableSearchFeature(value);
                resolve();
              })
              .catch(() => {
                if (!value) {
                  resolve();
                } else {
                  reject(new Error("Search feature can't be enabled"));
                }
              });

            break;
          }
          case "DOWNLOAD": {
            stores.appStore.enableDownloadFeature(value);
            resolve();
            break;
          }
          case "PRINT": {
            stores.appStore.enablePrintFeature(value);
            resolve();
            break;
          }
          case "TEXT_SELECTION": {
            stores.appStore.enableTextSelection(value);
            resolve();
            break;
          }
          case "EDIT": {
            let usePartialEdit = false;
            if (allConfigs && allConfigs.appConfig && isBoolean(allConfigs.appConfig.usePartialEdit)) {
              usePartialEdit = allConfigs.appConfig.usePartialEdit;
            }
            if (allConfigs && allConfigs.appConfig && isBoolean(allConfigs.appConfig.shouldEnableEditGenAI)) {
              stores.appStore.setShouldEnableEditGenAI(allConfigs.appConfig.shouldEnableEditGenAI);
            }
            if (allConfigs && allConfigs.appConfig && isBoolean(allConfigs.appConfig.shouldPreloadEditModule)) {
              stores.overlayDropinStore.setShouldPreloadEditModule(allConfigs.appConfig.shouldPreloadEditModule);
            }
            if (allConfigs && allConfigs.appConfig && allConfigs.appConfig.disableEditFeatures) {
              if (
                isBoolean(allConfigs.appConfig.disableEditFeatures.disableFormatText) &&
                isBoolean(allConfigs.appConfig.disableEditFeatures.disableBBox)
              ) {
                stores.appStore.setDisableEditFeatures({
                  disableFormatText: allConfigs.appConfig.disableEditFeatures.disableFormatText,
                  disableBBox: allConfigs.appConfig.disableEditFeatures.disableBBox
                });
              } else if (
                isBoolean(allConfigs.appConfig.disableEditFeatures.disableFormatText) &&
                !isBoolean(allConfigs.appConfig.disableEditFeatures.disableBBox)
              ) {
                stores.appStore.setDisableEditFeatures({
                  disableFormatText: allConfigs.appConfig.disableEditFeatures.disableFormatText
                });
              } else if (
                !isBoolean(allConfigs.appConfig.disableEditFeatures.disableFormatText) &&
                isBoolean(allConfigs.appConfig.disableEditFeatures.disableBBox)
              ) {
                stores.appStore.setDisableEditFeatures({
                  disableBBox: allConfigs.appConfig.disableEditFeatures.disableBBox
                });
              }
            }
            if (allConfigs && allConfigs.appConfig && isBoolean(allConfigs.appConfig.isDemoMode)) {
              stores.overlayDropinStore.setIsDemoMode(allConfigs.appConfig.isDemoMode);
            }
            if (allConfigs && allConfigs.appConfig && isBoolean(allConfigs.appConfig.showEditOnTextSelection)) {
              stores.overlayDropinStore.setShowEditOnTextSelection(allConfigs.appConfig.showEditOnTextSelection);
            }
            if (value) trackEditEnabled(usePartialEdit);
            let renderEditValue = EDIT_DROPIN_TYPE.NONE;

            if (value) {
              renderEditValue = usePartialEdit ? EDIT_DROPIN_TYPE.PARTIAL : EDIT_DROPIN_TYPE.FULL;
            }

            const { getFeatureFlag } = getFeatureFlagApis();
            getFeatureFlag(ENABLE_PREMIUM_INDICATOR)
              .then(enablePremiumIndicator => {
                if (typeof enablePremiumIndicator === "boolean") {
                  stores.appStore.setUsePremiumIndicator(enablePremiumIndicator);
                }
              })
              .finally(() => {
                stores.overlayDropinStore.setShouldRenderEditDropin(renderEditValue);
                resolve();
              });
            break;
          }
          case "INTERACTIVE_TOOLTIP": {
            stores.appStore.setShouldShowInteractiveTooltips(value);
            resolve();
            break;
          }
          case "ACROFORM_VALIDATION_FORMATTING": {
            stores.appStore.setAcroformValidationFormatting(value);
            resolve();
            break;
          }
          case "PDF_JAVASCRIPT": {
            stores.appStore.setEnablePDFJavaScriptFeatureFlag(value);
            resolve();
            break;
          }
          case "FILL_SIGN": {
            isFillSignEnabled({ enableFillSign: value }).then(enableFillSign => {
              if (enableFillSign) registerFillAndSignApis(stores);
              stores.overlayDropinStore.setShouldRenderFillSignDropin(value);
              resolve();
            });
            break;
          }
          case "OVERPRINT_SUPPORT": {
            stores.appStore.setEnableOverprintSupport(value);
            resolve();
            break;
          }
          case "GENAI_QNA": {
            stores.appStore.setShouldEnableQnA(value);
            if (value) {
              trackIsEligibleUser(ANALYTICS_ASSISTANT);
            }
            break;
          }
          case "GENAI_OUTLINE": {
            stores.appStore.setShouldEnableOutline(value);
            if (value) {
              trackIsEligibleUser(ANALYTICS_OUTLINE);
            }
            break;
          }
          case "ASSISTANT_PROMO": {
            stores.appStore.setShouldShowAnimation(value);
            resolve();
            break;
          }
          default: {
            reject(new Error("This feature is not supported"));
          }
        }
      } else {
        reject(new Error("Please provide a valid value"));
      }
    });
  }

  return {
    enableFeature
  };
};
